import React from 'react';
import { styled } from '@this/constants/themes';
import { Link } from '@this/shared/ui/navigations/link';
import Notification from '@this/src/notification';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import type TransportElementAttachFile from '@this/src/domain/transport_element_attach_file';
import type TravelerInformation from '@this/src/domain/traveler_information';
import type ElementAttachFile from '@this/domain/element_attach_file';

type AttachFile = TransportElementAttachFile | ElementAttachFile;

// 追加ボタンで挿入された行。ファイル選択後は uploadFileName / file が埋まる（保存済みのAttachFileとは別物）
type PlaceholderFile = {
  travelerInformationId: string | null;
  uploadFileName: string;
  isPlaceholder: true;
  file?: File;
};

type LocalAttachFile = AttachFile | PlaceholderFile;

const isPlaceholderFile = (file: LocalAttachFile): file is PlaceholderFile => 'isPlaceholder' in file;

// Recordのキーは文字列のみのため未割り当てを 'null' で表すが、API送信値は本来のnullに戻す
const UNASSIGNED_KEY = 'null';
const toTravelerInformationId = (travelerIdKey: string): string | null =>
  travelerIdKey === UNASSIGNED_KEY ? null : travelerIdKey;

type Props = {
  className?: string;
  attachFiles?: AttachFile[];
  travelerInformations: TravelerInformation[];
  attachableType?: string;
  attachableId?: number;
  onChange: (file: File, transportElementId: number, travelerInformationId: string | null) => void;
  onDelete?: (transportElementId: number, travelerInformationId: string | null, index: number) => void;
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#007bff',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0
};

// 氏名未入力の出張者でもセクションを区別できるようにする
const travelerDisplayName = (traveler: TravelerInformation, index: number) => {
  const name = `${traveler.lastNameKana || ''} ${traveler.firstNameKana || ''}`.trim();
  return name || `出張者${index + 1}`;
};

export const TransportElementAttachFileForm = ({
  className,
  attachFiles = [],
  travelerInformations,
  attachableType,
  attachableId,
  onChange,
  onDelete
}: Props) => {
  const [localAttachFiles, setLocalAttachFiles] = React.useState(() => {
    return attachFiles.reduce((acc, file) => {
      const travelerId = file.travelerInformationId?.toString() ?? UNASSIGNED_KEY;
      if (!acc[travelerId]) acc[travelerId] = [];
      acc[travelerId].push(file);
      return acc;
    }, {} as { [travelerId: string]: LocalAttachFile[] });
  });

  // id未設定の出張者は除外し、型ガードでid:numberを確定（map側で型注釈すると無効になる）
  const selectableTravelers = travelerInformations.filter(
    (traveler): traveler is TravelerInformation & { id: number } => traveler.id !== undefined
  );

  const handleAddInputField = (travelerIdKey: string) => {
    const newPlaceholder: PlaceholderFile = {
      travelerInformationId: toTravelerInformationId(travelerIdKey),
      uploadFileName: '',
      isPlaceholder: true
    };

    setLocalAttachFiles(prev => ({
      ...prev,
      [travelerIdKey]: [...(prev[travelerIdKey] || []), newPlaceholder]
    }));
  };

  const handleRemoveInputField = (travelerIdKey: string, index: number) => {
    setLocalAttachFiles(prev => {
      const updated = { ...prev };
      updated[travelerIdKey] = updated[travelerIdKey]?.filter((_, i) => i !== index);
      return updated;
    });

    if (onDelete && attachableId !== undefined) {
      onDelete(attachableId, toTravelerInformationId(travelerIdKey), index);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, travelerIdKey: string, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      const errorMessage = 'ファイルサイズが大きすぎます。10MB以下のファイルを選択してください。';
      e.target.value = '';
      Notification.error(errorMessage);
      return;
    }

    const travelerInformationId = toTravelerInformationId(travelerIdKey);
    setLocalAttachFiles(prev => {
      const updated = { ...prev };
      const files = [...(updated[travelerIdKey] || [])];
      files[index] = {
        travelerInformationId,
        uploadFileName: file.name,
        isPlaceholder: true,
        file
      };
      updated[travelerIdKey] = files;
      return updated;
    });

    if (attachableId !== undefined) {
      onChange(file, attachableId, travelerInformationId);
    }
  };

  const generateDownloadPath = (type?: string) => (file: AttachFile) => {
    const pathMap: Record<string, string> = {
      wifi: 'element_attach_files',
      transport: 'transport_element_attach_files'
    };
    const dir = pathMap[type || 'transport'] ?? 'transport_element_attach_files';
    return `/arrangement/trips/${file.tripId}/${dir}/${file.id}.pdf`;
  };

  const downloadPath = generateDownloadPath(attachableType);

  const renderSection = (travelerIdKey: string, title: string) => (
    <div key={travelerIdKey} style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <h4>{title}</h4>
        <button type="button" onClick={() => handleAddInputField(travelerIdKey)} style={linkButtonStyle}>
          ファイルを追加
        </button>
      </div>
      {localAttachFiles[travelerIdKey]?.map((file, index) => (
        <FileSection key={index}>
          {isPlaceholderFile(file) ? (
            file.file ? (
              <>
                <PdfIcon style={{ marginRight: '5px' }} />
                {file.uploadFileName}
              </>
            ) : (
              <input
                type="file"
                name="files[]"
                onChange={e => handleChange(e, travelerIdKey, index)}
                accept="application/pdf"
              />
            )
          ) : file.filePath ? (
            <>
              <PdfIcon style={{ marginRight: '5px' }} />
              <Link href={downloadPath(file)} target="_blank" rel="noopener noreferrer" isExternal>
                {file.uploadFileName}
              </Link>
            </>
          ) : (
            <>
              <PdfIcon style={{ marginRight: '5px' }} />
              {file.uploadFileName}
            </>
          )}
          <button
            type="button"
            onClick={() => handleRemoveInputField(travelerIdKey, index)}
            style={linkButtonStyle}
          >
            削除
          </button>
        </FileSection>
      ))}
    </div>
  );

  return (
    <Root className={className}>
      {selectableTravelers.map((traveler, i) =>
        renderSection(traveler.id.toString(), travelerDisplayName(traveler, i))
      )}
      {renderSection(UNASSIGNED_KEY, '出張者未割り当て')}
    </Root>
  );
};

const Root = styled.div`
  background-color: ${props => props.theme.grayColorLight};
  padding: 10px 5px 5px 5px;
  width: 100%;
`;

const FileSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 5px;
  margin-bottom: 5px;
  margin-left: 10px;
  padding: 2px 0 2px 10px;
  font-size: 12px;
`;
